import {
  collection, doc, getDocs, deleteDoc,
  serverTimestamp, onSnapshot, query, orderBy, runTransaction, where
} from 'firebase/firestore';
import { db, uidOrThrow, auth } from '../services/firebaseConfig';
import { FirestoreAddress } from '../types/address';
import { enrichAddresses } from './addressEnrichmentService';

const colRef = (userId: string) => collection(db, 'Users', userId, 'Addresses');

// Get user addresses (subcollection under user document)
// Firestore rule: allow list, get - if authed() && (userId == uid() || isAdmin())
// SECURITY: IDOR Protection - Validates uid matches authenticated user
export async function getUserAddresses(uid: string): Promise<(FirestoreAddress & { id: string })[]> {
  // Firestore rule compliance: verify uid matches authenticated user
  // SECURITY: IDOR Protection - Verify uid matches authenticated user
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User must be authenticated to view addresses');
  }
  
  if (uid !== currentUser.uid) {
    // SECURITY: Log unauthorized access attempt
    // SECURITY: Removed console.warn to prevent information leakage
    throw new Error('You can only view your own addresses');
  }
  
  const snap = await getDocs(colRef(uid));
  return snap.docs.map(d => ({ ...d.data() as FirestoreAddress, id: d.id }));
}

// SECURITY: IDOR Protection - Validates uid matches authenticated user
export async function getSelectedAddress(uid: string): Promise<FirestoreAddress | null> {
  // SECURITY: IDOR Protection - getUserAddresses will validate ownership
  const list = await getUserAddresses(uid);
  return list.find(a => a.SelectedAddress) ?? null;
}

export async function listAddresses(): Promise<(FirestoreAddress & { id: string })[]> {
  const userId = uidOrThrow();
  const snap = await getDocs(colRef(userId));
  const addresses = snap.docs.map(d => ({ ...d.data() as FirestoreAddress, id: d.id }));
  // Sort default first, newest next
  addresses.sort((a, b) => Number(b.SelectedAddress) - Number(a.SelectedAddress));
  // Enrich addresses with delivery rate data
  const enrichedAddresses = enrichAddresses(addresses);
  return enrichedAddresses as (FirestoreAddress & { id: string })[];
}

export function watchAddresses(cb: (items: (FirestoreAddress & { id: string })[]) => void) {
  const userId = uidOrThrow();
  const q = query(colRef(userId), orderBy('SelectedAddress', 'desc'), orderBy('DateTime', 'desc'));
  
  return onSnapshot(q, (snap) => {
    const addresses = snap.docs.map(d => ({ ...d.data() as FirestoreAddress, id: d.id }));
    addresses.sort((a, b) => Number(b.SelectedAddress) - Number(a.SelectedAddress));
    // Enrich addresses with delivery rate data
    const enrichedAddresses = enrichAddresses(addresses);
    cb(enrichedAddresses as (FirestoreAddress & { id: string })[]);
  });
}

// Create or update address (subcollection under user document)
// Firestore rule: allow create, update - if authed() && (userId == uid() || isAdmin())
// SECURITY: IDOR Protection - Validates uid matches authenticated user
export async function upsertAddress(
  uid: string,
  addressId: string | null,
  payload: Omit<FirestoreAddress, "DateTime">
) {
  // Firestore rule compliance: verify uid matches authenticated user
  // SECURITY: IDOR Protection - Verify uid matches authenticated user
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User must be authenticated to manage addresses');
  }
  
  if (uid !== currentUser.uid) {
    // SECURITY: Log unauthorized access attempt
    // SECURITY: Removed console.warn to prevent information leakage
    throw new Error('You can only manage your own addresses');
  }
  
  const userRef = doc(db, "Users", uid);
  const colRef = collection(userRef, "Addresses");

  await runTransaction(db, async (tx) => {
    // If this one should be selected, unselect others first
    if (payload.SelectedAddress) {
      const q = query(colRef, where("SelectedAddress", "==", true));
      const current = await getDocs(q);
      for (const d of current.docs) {
        tx.update(d.ref, { SelectedAddress: false });
      }
    }

    const newData: FirestoreAddress = {
      ...payload,
      DateTime: serverTimestamp() as any, // Firestore Timestamp at write
    };

    const ref = addressId ? doc(colRef, addressId) : doc(colRef);
    // Removed debug log to prevent sensitive data exposure (addressId)
    // set with merge false to avoid stray lowercase keys
    tx.set(ref, newData, { merge: false });
  });
}

export async function createAddress(data: Omit<FirestoreAddress, "DateTime">) {
  const userId = uidOrThrow();
  
  // Check if address with same name already exists
  const existingAddresses = await getUserAddresses(userId);
  const existingAddress = existingAddresses.find(a => a.Name === data.Name);
  
  if (existingAddress) {
    throw new Error('An address with this name already exists. Please use a different name or edit the existing address.');
  }
  
  const id = (typeof crypto !== 'undefined' && 'randomUUID' in crypto) ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
  
  await upsertAddress(userId, id, data);
  return id;
}

export async function updateAddress(id: string, data: Partial<FirestoreAddress>) {
  const userId = uidOrThrow();
  const currentAddresses = await getUserAddresses(userId);
  const current = currentAddresses.find(a => a.id === id); // Find by document ID
  
  if (!current) {
    throw new Error('Address not found');
  }

  const updatedData = { ...current, ...data };
  await upsertAddress(userId, id, updatedData);
}

// Delete address (subcollection under user document)
// Firestore rule: allow delete - if authed() && (userId == uid() || isAdmin())
// Compliance: uidOrThrow() ensures user is authenticated and userId matches authenticated user's uid
export async function deleteAddress(id: string) {
  const userId = uidOrThrow(); // Ensures authentication and returns authenticated user's uid
  await deleteDoc(doc(colRef(userId), id));
}

export async function setDefaultAddress(id: string) {
  const userId = uidOrThrow();
  const currentAddresses = await getUserAddresses(userId);
  const targetAddress = currentAddresses.find(a => a.id === id); // Find by document ID
  
  if (!targetAddress) {
    throw new Error('Address not found');
  }

  await upsertAddress(userId, id, { ...targetAddress, SelectedAddress: true });
}




